import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  MdQuestionAnswer,
  MdMobileFriendly,
  MdTrackChanges,
} from "react-icons/md";

import {
  FaGlobe,
  FaFacebookF,
  FaInstagram,
  FaArrowRight,
  FaArrowLeft,
} from "react-icons/fa";

import { useTranslation } from "react-i18next";

import "./ContactPage.css";
import logo from "./assets/contact.png";

const ContactPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [showLangMenu, setShowLangMenu] = useState(false);

  // ============================================================
  // MOUSE GLOW
  // ============================================================

  useEffect(() => {
    const handleMouseMove = (e) => {
      const container = document.querySelector(
        ".contactpage-container"
      );

      if (!container) return;

      const rect = container.getBoundingClientRect();

      const x =
        ((e.clientX - rect.left) / rect.width) * 100;

      const y =
        ((e.clientY - rect.top) / rect.height) * 100;

      container.style.setProperty(
        "--glow-x",
        `${x}%`
      );

      container.style.setProperty(
        "--glow-y",
        `${y}%`
      );
    };

    window.addEventListener(
      "mousemove",
      handleMouseMove
    );

    return () => {
      window.removeEventListener(
        "mousemove",
        handleMouseMove
      );
    };
  }, []);

  // ============================================================
  // LANGUAGE
  // ============================================================

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);

    document.dir =
      lang === "ar" ? "rtl" : "ltr";

    setShowLangMenu(false);
  };

  const isArabic = i18n.language === "ar";

  // ============================================================
  // ACTIONS
  // ============================================================

const actions = [
  {
    id: "question",
    icon: <MdQuestionAnswer />,
    path: "/ask-question",
    eyebrow: t("// GENERAL INQUIRY"),
    title: t("Ask a Question"),
    description: t(
      "Have a question about our services, technology, or how we can help?"
    ),
  },
  {
    id: "project",
    icon: <MdMobileFriendly />,
    path: "/build-project",
    eyebrow: t("// PROJECT INITIATION"),
    title: t("Build a Project"),
    description: t(
      "Have an idea for an app or website? Let's turn it into something real."
    ),
  },
  {
    id: "tracking",
    icon: <MdTrackChanges />,
    path: "/order-tracking",
    eyebrow: t("// ORDER TRACKING"),
    title: t("Track Your Order"),
    description: t(
      "Already submitted a project? Track your order and check its current status."
    ),
  },
];

  return (
    <div className="contactpage-container">

      {/* ========================================================
          AMBIENT GLOW
      ======================================================== */}

      <div
        className="ambient-glow"
        aria-hidden="true"
      />

      {/* ========================================================
          BACKGROUND GRID
      ======================================================== */}

      <div
        className="contact-grid"
        aria-hidden="true"
      />

      {/* ========================================================
          LANGUAGE SWITCHER
      ======================================================== */}

      <div className="about-topbar">

        <div className="lang-switcher">

          <button
            type="button"
            className="lang-toggle"
            onClick={() =>
              setShowLangMenu((value) => !value)
            }
            aria-label={t("Toggle language menu")}
            aria-expanded={showLangMenu}
          >
            <FaGlobe size={16} />

            <span>
              {isArabic ? "AR" : "EN"}
            </span>
          </button>

          {showLangMenu && (
            <div className="language-dropdown">

              <div
                onClick={() =>
                  changeLanguage("en")
                }
              >
                {t("English")}
              </div>

              <div
                onClick={() =>
                  changeLanguage("ar")
                }
              >
                {t("Arabic")}
              </div>

            </div>
          )}

        </div>

      </div>

      {/* ========================================================
          MAIN
      ======================================================== */}

      <main className="contactpage-main">

        {/* SYSTEM LABEL */}

        <div
          className="contact-system-label reveal"
          style={{
            "--delay": "0.05s",
          }}
        >
          <span className="system-dot" />
          <span>
            {t("EMKA // CONNECTION POINT")}
          </span>
        </div>

        {/* LOGO */}

        <div
          className="contact-logo-wrapper reveal"
          style={{
            "--delay": "0.1s",
          }}
        >
          <div className="logo-ring" />

          <img
            src={logo}
            alt="EMKA"
            className="contactpage-logo"
          />
        </div>

        {/* TITLE */}

        <div
          className="contact-heading reveal"
          style={{
            "--delay": "0.2s",
          }}
        >

          <span className="eyebrow">
            {t("// EMKA — LET'S BUILD SOMETHING")}
          </span>

          <h1 className="contactpage-title">
            {t("Contact Us")}
          </h1>

          <p className="contactpage-subtitle">
            {t(
              "Whether you have a question or a project in mind, choose where you want to start."
            )}
          </p>

        </div>

        {/* ========================================================
            ACTION CARDS
        ======================================================== */}

        <div className="contactpage-buttons">

          {actions.map((action, index) => (

            <button
              key={action.id}
              type="button"
              className="contact-action-card reveal"
              style={{
                "--delay": `${0.3 + index * 0.12}s`,
              }}
              onClick={() =>
                navigate(action.path)
              }
            >

              {/* Top line */}

              <div className="action-top">

                <span className="action-eyebrow">
                  {action.eyebrow}
                </span>

                <span className="action-index">
                  0{index + 1}
                </span>

              </div>

              {/* Icon */}

              <div className="action-icon">
                {action.icon}
              </div>

              {/* Text */}

              <div className="action-content">

                <h2>
                  {action.title}
                </h2>

                <p>
                  {action.description}
                </p>

              </div>

              {/* Arrow */}

              <div className="action-footer">

                <span>
                  {t("Continue")}
                </span>

                <span className="action-arrow">

                  {isArabic ? (
                    <FaArrowLeft />
                  ) : (
                    <FaArrowRight />
                  )}

                </span>

              </div>

              {/* Scan line */}

              <div className="card-scan-line" />

            </button>

          ))}

        </div>

        {/* ========================================================
            BOTTOM MESSAGE
        ======================================================== */}

        <div
          className="contact-bottom-message reveal"
          style={{
            "--delay": "0.6s",
          }}
        >
          <span className="bottom-line" />

          <span>
            {t(
              "From an idea to a working product — EMKA is ready."
            )}
          </span>

          <span className="bottom-line" />
        </div>

        {/* ========================================================
            SOCIAL
        ======================================================== */}

        <div
          className="bottom-social-row reveal"
          style={{
            "--delay": "0.7s",
          }}
        >

          <a
            href="https://www.facebook.com/EmkaCode.tech"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FaFacebookF />
          </a>

          <a
            href="https://www.instagram.com/emkacode/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <FaInstagram />
          </a>

        </div>

        {/* SYSTEM FOOTER */}

        <div
          className="contact-system-footer reveal"
          style={{
            "--delay": "0.8s",
          }}
        >
          {t("EMKA_CORE // READY TO RECEIVE YOUR IDEA")}
        </div>

      </main>
    </div>
  );
};

export default ContactPage;